import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2, Clock, FileText, GraduationCap, LogOut, Sparkles, ArrowRight, MessageCircle,
} from 'lucide-react';
import ChatPanel from '../components/ChatPanel';
import StudentApplicationSection from '../components/StudentApplicationSection';
import BookAppointment from '../components/BookAppointment';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 0.9, 0.32, 1] },
  }),
};

// Only the stages the student is allowed to see. Anything past LOA Received
// is internal ops (NOA, contracts, commissions) — for those we swap the
// whole progress tracker for a "Congratulations, enjoy your studies!" card.
const STUDENT_STAGES = [
  { key: 'received', label: 'Received', desc: 'We received your application.' },
  { key: 'counsellor_assigned', label: 'Counsellor Assigned', desc: 'A counsellor is now looking after you.' },
  { key: 'college_assigned', label: 'College Assigned', desc: 'Your target college is picked.' },
  { key: 'sent_to_college', label: 'Sent to College', desc: 'Your application is with the college.' },
  { key: 'loa_received', label: 'LOA Received', desc: 'Your Letter of Acceptance has arrived.' },
];

const BOOKABLE_STAGES = new Set(['loa_received']);

export default function StudentDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('overview');
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (!meRes.ok) {
          if (meRes.status === 401) return navigate('/student-login');
          throw new Error('Failed to load profile');
        }
        const meData = await meRes.json();
        setMe(meData);
        // Future endpoint to fetch student profile by user_id; gracefully handle if unavailable.
        try {
          const stuRes = await fetch(`${API}/api/student/me`, { credentials: 'include' });
          if (stuRes.ok) setProfile(await stuRes.json());
        } catch (_) {}
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/chat/unread`, { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (mounted) setUnreadTotal(d.total || 0);
      } catch (_) {}
    };
    tick();
    const id = setInterval(tick, 10_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  async function handleLogout() {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/');
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-tge-paper flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tge-navy" />
      </div>
    );
  }

  // Student always sees the 5-stage flow (Received → LOA Received). The
  // backend caps `pipeline_stage` at `loa_received` and sets
  // `pipeline_completed` when the admin has moved the student past LOA.
  // From the student's perspective, LOA Received IS the finish line — so
  // once they hit that stage (or beyond) every visible card is marked
  // "Done"; the tracker never leaves LOA in the "Current" state.
  const stages = STUDENT_STAGES;
  const pipelineCompleted = Boolean(profile?.pipeline_completed);
  const currentKey = profile?.pipeline_stage || 'received';
  const currentStageIdx = stages.findIndex((s) => s.key === currentKey);
  const allDone = pipelineCompleted || currentKey === 'loa_received';
  const stageIdx = allDone
    ? stages.length
    : (currentStageIdx === -1 ? 0 : currentStageIdx);
  // For the hero title we still want to show "LOA Received" once the
  // student is done, since it's the milestone they should celebrate.
  const heroStage = allDone ? stages[stages.length - 1] : stages[stageIdx];

  return (
    <div className="min-h-screen bg-tge-paper" data-testid="student-dashboard">
      <header className="bg-white/70 backdrop-blur-md border-b border-tge-navy/5 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-tge-gold font-semibold">Student Portal</div>
            <h1 className="font-serif-display text-2xl text-tge-navy mt-0.5">
              Hi {me?.name?.split(' ')[0] || 'Student'} 👋
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView(view === 'messages' ? 'overview' : 'messages')}
              className="relative border border-tge-navy/15 text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 hover:bg-white transition"
              data-testid="student-messages-toggle"
            >
              <MessageCircle size={16} /> Messages
              {unreadTotal > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center font-semibold">
                  {unreadTotal}
                </span>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="border border-tge-navy/15 text-tge-navy font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 hover:bg-white transition"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl">{error}</div>
        )}

        {view === 'messages' ? (
          <ChatPanel canInitiate={false} currentUserId={me?.id} onUnreadChange={setUnreadTotal} />
        ) : (
        <>
        {/* Hero progress card OR completion celebration */}
        {pipelineCompleted ? (
          <motion.section
            initial="hidden"
            animate="show"
            variants={fadeUp}
            className="relative bg-gradient-to-br from-tge-navy via-tge-navy to-emerald-900 text-white rounded-3xl p-8 sm:p-12 overflow-hidden text-center"
            data-testid="student-completion-card"
          >
            <div className="absolute -top-16 -right-16 w-80 h-80 bg-tge-gold/25 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-10 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-wider text-tge-gold font-semibold">
                <Sparkles size={11} /> All done
              </span>
              <h2 className="font-serif-display text-4xl sm:text-6xl mt-5 leading-tight">
                Congratulations! 🎉
              </h2>
              <p className="text-tge-cream/85 mt-4 text-lg max-w-xl mx-auto">
                Enjoy your studies. You&apos;ve completed every step with us — we&apos;re proud to have been part of your journey.
              </p>
            </div>
          </motion.section>
        ) : (
        <motion.section
          initial="hidden"
          animate="show"
          variants={fadeUp}
          className="relative bg-tge-navy text-white rounded-3xl p-8 sm:p-10 overflow-hidden"
        >
          <div className="absolute -top-16 -right-16 w-72 h-72 bg-tge-gold/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-tge-gold/10 rounded-full blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-wider text-tge-gold font-semibold">
              <Sparkles size={11} /> Application status
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-4" data-testid="student-current-stage">
              {heroStage?.label || 'Getting started'}
            </h2>
            <p className="text-tge-cream/75 mt-3 max-w-xl">
              {heroStage?.desc || 'Your counsellor will reach out within 24 hours.'}
            </p>

            <div className="mt-9">
              <ProgressBar stageIdx={Math.min(stageIdx, stages.length - 1)} total={stages.length} />
            </div>
          </div>
        </motion.section>
        )}

        {/* Stage cards — hidden once the student has completed the journey */}
        {!pipelineCompleted && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stages.map((s, i) => {
            const done = i < stageIdx;
            const current = i === stageIdx;
            return (
              <motion.div
                key={s.key}
                custom={i}
                initial="hidden"
                animate="show"
                variants={fadeUp}
                className={`bg-white rounded-2xl p-5 border transition ${current ? 'border-tge-gold shadow-lg' : 'border-tge-navy/5'}`}
              >
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-semibold mb-3"
                  style={{ color: done ? '#5fb487' : current ? '#c8a035' : '#9ca0a9' }}
                >
                  {done ? <CheckCircle2 size={14} /> : <Clock size={14} />} {done ? 'Done' : current ? 'Current' : 'Up next'}
                </div>
                <h3 className="font-serif-display text-xl text-tge-navy">{s.label}</h3>
                <p className="text-sm text-tge-muted mt-1">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
        )}

        {/* Profile + Next steps */}
        <div className="grid md:grid-cols-2 gap-4">
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={5} className="bg-white rounded-2xl p-6 border border-tge-navy/5">
            <h3 className="font-serif-display text-xl text-tge-navy mb-4 flex items-center gap-2">
              <FileText size={18} className="text-tge-gold" /> Your profile
            </h3>
            <dl className="space-y-3 text-sm">
              <Row label="Email" value={me?.email} />
              <Row label="Name" value={me?.name} />
              {profile && (
                <>
                  <Row label="Phone" value={profile.phone} />
                  <Row label="Assigned college" value={profile.assigned_college_name || 'Not yet'} />
                </>
              )}
            </dl>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={6} className="bg-white rounded-2xl p-6 border border-tge-navy/5">
            <h3 className="font-serif-display text-xl text-tge-navy mb-4 flex items-center gap-2">
              <GraduationCap size={18} className="text-tge-gold" /> What's next
            </h3>
            <ul className="space-y-3 text-sm text-tge-navy">
              {[
                'Watch your inbox — your counsellor will email within 24 hours.',
                'Questions? Either email us or send us a message.',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full bg-tge-cream text-tge-gold flex items-center justify-center text-[11px] font-semibold flex-shrink-0">{i + 1}</span>
                  <span className="leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <a
              href="mailto:info@trueglobaleducation.com"
              className="inline-flex items-center gap-2 text-tge-gold font-semibold text-sm mt-5 hover:underline"
            >
              Email your counsellor <ArrowRight size={14} />
            </a>
          </motion.div>
        </div>

        {/* Video-call booking — unlocks at LOA Received */}
        {BOOKABLE_STAGES.has(profile?.pipeline_stage) && (
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={6}>
            <BookAppointment />
          </motion.div>
        )}

        {/* My application — view / edit / documents / what was sent */}
        <motion.div initial="hidden" animate="show" variants={fadeUp} custom={7}>
          <StudentApplicationSection profile={profile} onProfileUpdate={setProfile} />
        </motion.div>
        </>
        )}
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-tge-cream pb-2 last:border-0">
      <span className="text-tge-muted text-xs uppercase tracking-wider">{label}</span>
      <span className="text-tge-navy font-medium">{value || '—'}</span>
    </div>
  );
}

function ProgressBar({ stageIdx, total }) {
  const pct = ((stageIdx + 1) / total) * 100;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-tge-cream/60 mb-2">
        <span>Step {stageIdx + 1} of {total}</span>
        <span>{Math.round(pct)}% complete</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="h-full bg-gradient-to-r from-tge-gold to-tge-gold-soft rounded-full"
        />
      </div>
    </div>
  );
}
